import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import type { LoginResponse, ProblemDetail } from './types'

/**
 * Access token YALNIZCA bellekte tutulur — localStorage'a yazılmaz.
 *
 * <p>Böylece bir XSS açığı uzun ömürlü kimlik bilgisi çalamaz. Kalıcılığı
 * sağlayan şey HttpOnly refresh cookie'sidir; sayfa yenilendiğinde
 * /api/auth/refresh ile yeni bir access token alınır.
 */
let accessToken: string | null = null

type AuthListener = (reason: 'expired' | 'disabled' | 'revoked') => void
let onAuthLost: AuthListener | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}
export function getAccessToken() {
  return accessToken
}
export function setAuthLostHandler(fn: AuthListener | null) {
  onAuthLost = fn
}

export const api = axios.create({
  baseURL: '',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

/**
 * Oturum gerektirmeyen uçlar için ayrı istemci.
 *
 * <p>Paylaşım linkiyle gelen ziyaretçinin oturumu YOKTUR. Normal istemci
 * kullanılsaydı 401 alındığında /api/auth/refresh denenir, o da başarısız
 * olunca "oturumunuz düştü" akışı tetiklenirdi — hiç giriş yapmamış birine
 * gösterilecek en yanlış mesaj. Bu istemci token eklemez ve yenileme denemez.
 */
export const publicApi = axios.create({
  baseURL: '',
  headers: { 'Content-Type': 'application/json' },
})

/**
 * Tek uçuşta yenileme.
 *
 * <p>Sayfa açılışında onlarca istek aynı anda 401 alabilir. Her biri ayrı
 * bir /refresh çağırsaydı rotasyon zinciri kendi kendini "yeniden kullanım"
 * sanıp tüm oturumu iptal ederdi. Bu yüzden ilk çağrı bir promise başlatır,
 * diğerleri onu bekler.
 *
 * <p><b>Oturum kurtarma da BURADAN geçmek zorunda.</b> {@code AuthGate}
 * daha önce {@code authApi.refresh()}'i doğrudan çağırıyordu ve bu korumayı
 * atlıyordu; React StrictMode effect'i iki kez çalıştırdığı için sayfa
 * yenilendiğinde İKİ yenileme gidiyordu. İkincisi rotasyonla çoktan
 * geçersizleşmiş token'ı sunduğu için sunucu bunu token hırsızlığı sayıp
 * {@code REFRESH_TOKEN_REUSE} ile bütün oturumları düşürüyordu — kullanıcı
 * arada bir, sayfayı yenilediğinde sebepsizce giriş ekranına atılıyordu.
 */
let refreshPromise: Promise<LoginResponse> | null = null

const postRefresh = () =>
  axios.post<LoginResponse>('/api/auth/refresh', null, { withCredentials: true })

/**
 * Başka SEKME aynı anda yenilediyse sunucu bu isteği (az önce değiştirilmiş
 * token) 401 REFRESH_TOKEN_INVALID ile reddeder ama oturumu iptal etmez; o
 * sekmenin yanıtı çerezi yenilemiştir. Kısa bir beklemeyle bir kez yeniden denenir.
 */
async function postRefreshWithTabRetry() {
  try {
    return await postRefresh()
  } catch (e) {
    const code = (e as { response?: { status?: number; data?: { code?: string } } })?.response
    if (code?.status !== 401 || code.data?.code !== 'REFRESH_TOKEN_INVALID') throw e
    await new Promise((r) => setTimeout(r, 500))
    return postRefresh()
  }
}

/** Refresh cookie'si ile oturumu tazeler. Eşzamanlı çağrılar tek isteğe indirgenir. */
export async function refreshSession(): Promise<LoginResponse> {
  if (!refreshPromise) {
    refreshPromise = postRefreshWithTabRetry()
      .then((res) => {
        accessToken = res.data.accessToken
        return res.data
      })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

const NO_RETRY_PATHS = ['/api/auth/login', '/api/auth/refresh']

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ProblemDetail>) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean }
    const status = error.response?.status
    const code = error.response?.data?.code

    // Hesap kapatıldı veya oturum iptal edildi: yenilemeye çalışmak anlamsız,
    // kullanıcıyı hemen bilgilendir.
    if (status === 401 && (code === 'ACCOUNT_DISABLED' || code === 'SESSION_REVOKED')) {
      accessToken = null
      onAuthLost?.(code === 'ACCOUNT_DISABLED' ? 'disabled' : 'revoked')
      return Promise.reject(error)
    }

    const retryable =
      status === 401 &&
      original &&
      !original._retried &&
      !NO_RETRY_PATHS.some((p) => original.url?.includes(p))

    if (retryable) {
      original._retried = true
      try {
        const { accessToken: token } = await refreshSession()
        original.headers.Authorization = `Bearer ${token}`
        return api.request(original)
      } catch {
        accessToken = null
        onAuthLost?.('expired')
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  },
)

/** Hata gövdesinden kullanıcıya gösterilecek Türkçe metni çıkarır. */
export function errorMessage(err: unknown, fallback = 'Bir hata oluştu.'): string {
  const axiosErr = err as AxiosError<ProblemDetail>
  const data = axiosErr?.response?.data
  if (data?.fieldErrors?.length) {
    return data.fieldErrors.map((f) => f.message).join(' ')
  }
  return data?.detail || fallback
}

export function errorCode(err: unknown): string | undefined {
  return (err as AxiosError<ProblemDetail>)?.response?.data?.code
}

export function problem(err: unknown): ProblemDetail | undefined {
  return (err as AxiosError<ProblemDetail>)?.response?.data
}
